
import { Power0,gsap } from "gsap";

import { FontStyle, FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { Helper } from "../../../dev/utils/Helper";
import { MathHelper } from "../../../dev/utils/MathHelper";

import { BitmapTextStyleOptions, TextStyleOptions } from "../../../dev/setting/text/IText";
import { StageLayer } from "@nolimitcity/slot-launcher/bin/StageLayer";
import { TranslationTexts } from "../../config/translate/TranslationTexts";
import { Translator } from "../../../dev/setting/Translator";
import { GameAssets } from "../../config/texture/GameAssets";
import { BitmapCountUp } from "../../component/BitmapCountUp";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { GameConfig } from "../../config/base/GameConfig";
import { Sequence } from '../../../dev/displayobject/Sequence';
import { SoundManager } from '../../config/sound/SoundManager';
import { SoundConfig } from '../../config/sound/SoundConfig';

export class BonusCounters extends PIXI.Container implements StageLayer {

    private readonly _styleFs: TextStyleOptions = {
        fontFamily: "Helvetica",
        fontStyle: FontStyle.ITALIC,
        fontWeight: FontWeight.EXTRA_BOLD,
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 8,
        dropShadowDistance: 12,
        fill: [
            "#ffffff",
            "#FFFC00"
        ],
        fontSize: 72,
        miterLimit: 0,
        wordWrap:true,
        wordWrapWidth:500,
        stroke: "#FF6A00",
        strokeThickness: 2
    };

    private readonly _style: BitmapTextStyleOptions = {
        fontName: GameAssets.BITMAP_FONT_FS_NUMBERS,
        fontSize: 22,
        tint: 0xffffff,
        letterSpacing:0
    };

    private timeline: gsap.core.Timeline;
    private _txtMsg: PIXI.Text;

    private _fsAmount: BitmapCountUp;
    public static instance: BonusCounters;

    private _gfxBg: PIXI.Graphics;

    private respinSeq:Sequence;

    public get fsAmount(): number {
        return parseInt(this._fsAmount.text);
    }

    public setFsCounter(value: number): void {
        this._fsAmount.text = value.toString();
    }

    constructor() {
        super();
        BonusCounters.instance = this;
        this.name = "BonusCounter";
        this.init();
    }

    private init(): void {
        this.createBgGfx();
        this._gfxBg.alpha = 0;

        this._txtMsg = this.createText(Translator.translate(TranslationTexts.FREESPINS_LEFT), this._styleFs);
        this._fsAmount = this.addChild(new BitmapCountUp(this._style));
        this._fsAmount.alpha = 0;
        this._fsAmount.anchor = new PIXI.Point(0.5,0.6);

        this._fsAmount.y = 284;

        this.respinSeq = this.addChild(new Sequence({texturePath:"Respin"}));
        this.respinSeq.alpha = 0;
        this.respinSeq.position.y = 160;

        this.onResize();
    }

    private createText(text: string, style: TextStyleOptions): PIXI.Text {
        const textField = new PIXI.Text(text, style);
        textField.style.align = "center";
        textField.anchor.set(0.5, 0.5);
        textField.alpha = 0;
        this.addChild(textField);
        return textField;
    }

    protected createBgGfx(): void {
        const maxAreaSize: number = 3000;
        this._gfxBg = this.addChild(new PIXI.Graphics());
        this._gfxBg.beginFill(0x000000, 0.45);
        this._gfxBg.drawRect(-maxAreaSize * 0.5, -maxAreaSize * 0.5, maxAreaSize, maxAreaSize);
        this._gfxBg.endFill();
    }

    public playRespin(delay:number = 0.2): gsap.core.Timeline {

        this.timeline = Helper.setTimeline(this.timeline, new gsap.core.Timeline());

        this.timeline.add([
            gsap.fromTo(this._gfxBg, { alpha: 0 }, {duration:0.35, alpha: 1 }),
        ])

        this.timeline.add(this.respinSeq.play({onStart:() => { 
            this.respinSeq.alpha = 1;
            SoundManager.playSoundEffect( SoundConfig.RESPIN_POSTING);
        }}));

        this.timeline.to(this,{ duration: delay });

        this.timeline.to([this._gfxBg,this.respinSeq] , { alpha: 0,duration:0.25 },"-=" + 0.25) 

        return this.timeline;
    }

    public playMessage(msg:string, moveDurr:number = 0.8, delay:number = 0.2): gsap.core.Timeline {

        this.timeline = Helper.setTimeline(this.timeline, new gsap.core.Timeline());

        this._txtMsg.text = Translator.translate(msg);
        this._txtMsg.y = 192;

        this._txtMsg.scale.set(1, 1);

        const maxWidth = GameConfig.instance.BASE_DIM.portrait.WIDHT;

        while(this._txtMsg.width * this._txtMsg.scale.x > maxWidth) {
            this._txtMsg.scale = MathHelper.pointMul(<PIXI.Point>this._txtMsg.scale, 0.995);
            this._txtMsg.updateTransform();
        }

        this.timeline.add([
            gsap.fromTo(this._txtMsg, { alpha: 0 }, { duration:0.35, alpha: 1 }),
            gsap.fromTo(this._gfxBg, { alpha: 0 }, {duration:0.35, alpha: 1 }),
            gsap.fromTo(this._txtMsg.position, { x: 260 }, {duration:0.35, x: 340,  }),
        ])

        this.timeline.add([
            gsap.to(this._txtMsg.position, moveDurr * 1.5, { x: 380 }),
        ]).duration() - (moveDurr * 1.5);

        this.timeline.to(this,{duration:delay});

        this.timeline.add([
            gsap.to(this._txtMsg, 0.25, { alpha: 0 }),
            gsap.to(this._gfxBg, 0.25, { alpha: 0 }),
        ], "-=" + 0.15)

        return this.timeline;
    }

    public playFSCounter(amm: number = 0, from?: number): gsap.core.Timeline {

        this._txtMsg.text = Translator.translate(TranslationTexts.FREESPINS_LEFT);

        if (from != undefined) this._fsAmount.text = from.toString();

        this._txtMsg.y = 192;

        const countAmm: number = Math.abs(amm);
        const startValue: number = parseInt(this._fsAmount.text);

        this.timeline = Helper.setTimeline(this.timeline, new gsap.core.Timeline());

        const totalTickDurr: number = (countAmm * 0.2);
        const moveTime: number = 0.8 + totalTickDurr;

        if (startValue != 0 || amm > 0) {
            this._txtMsg.scale.set(1, 1);
            this._txtMsg.scale = MathHelper.pointMul(<PIXI.Point>this._txtMsg.scale, 600 / this._txtMsg.width);

            this.timeline.add([
                gsap.fromTo(this._txtMsg, 0.35, { alpha: 0 }, { alpha: 1 }),
                gsap.fromTo(this._fsAmount, 0.35, { alpha: 0 }, { alpha: 1 }),

                gsap.fromTo(this._txtMsg.position, 0.35, { x: 260 }, { x: 340, ease: Power0.easeNone }),
                gsap.fromTo(this._fsAmount.position, 0.35, { x: 460 }, { x: 380, ease: Power0.easeNone })
            ])

            const startMoveTime = this.timeline.add([
                gsap.to(this._txtMsg.position, moveTime, { x: 370 }),
                gsap.to(this._fsAmount.position, moveTime, { x: 350 })
            ]).duration() - (moveTime - totalTickDurr);

            let stepVal: number = startValue;
            for (let i = 0; i < countAmm; i++) {
                const showVal: string = (stepVal + (amm >= 0 ? 1 : -1)).toString();
                const countTimer: number = startMoveTime + (i * 0.2);

                this.timeline.add(() => {
                    this._fsAmount.text = showVal;
                    SlotGame.keypadPlugin.setZeroBetSpinCounter(parseInt(showVal));
                }, countTimer);
                this.timeline.to(this._fsAmount.scale, {duration:0.1, x: 1.15, y: 1.15 }, countTimer);
                this.timeline.to(this._fsAmount.scale, {duration:0.1, x: 1, y: 1 }, countTimer - 0.1);

                stepVal += (amm >= 0 ? 1 : -1);
            }

        } else {

            this._txtMsg.text = Translator.translate(TranslationTexts.LAST_SPIN);
            this._txtMsg.scale.set(1, 1)
            if (this._txtMsg.width < 500)
                this._txtMsg.scale = MathHelper.pointMul(<PIXI.Point>this._txtMsg.scale, 1.35);
            else {
                this._txtMsg.scale = MathHelper.pointMul(<PIXI.Point>this._txtMsg.scale, 500 / this._txtMsg.width);
            }

            this.timeline.add([
                gsap.fromTo(this._txtMsg, 0.35, { alpha: 0 }, { alpha: 1 }),
                gsap.fromTo(this._txtMsg.position, 0.35, { x: 260 }, { x: 340 }),
            ])

            this.timeline.add([
                gsap.to(this._txtMsg.position, moveTime * 1.5, { x: 380 }),
            ]).duration() - (moveTime * 1.5 - totalTickDurr);
        }

        this.timeline.add([
            gsap.to(this._txtMsg, 0.25, { alpha: 0 }),
            gsap.to(this._fsAmount, 0.25, { alpha: 0 }),
        ], "-=" + 0.15)

        return this.timeline;
    }

    public onResize(): void {
    }
}
